import { useCallback } from 'react';

import type { BuckChatConfig, OtpContext } from '../../../types';
import { ANALYTICS_KEYS, BUCK_EVENTS } from '../../../utils/analytics';

type UseSocialAuthOptions = {
  config: BuckChatConfig;
  context?: OtpContext;
  onAnalyticsEvent?: (event: string, data: Record<string, unknown>) => void;
};

type UseSocialAuthReturn = {
  handleGoogleAuth: () => void;
  handleFacebookAuth: () => void;
};

export const useSocialAuth = ({
  config,
  context,
  onAnalyticsEvent,
}: UseSocialAuthOptions): UseSocialAuthReturn => {
  const buildAuthUrl = useCallback(
    (authUrl: string) => {
      const url = new URL(authUrl, window.location.origin);
      if (context) {
        Object.entries(context).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            url.searchParams.set(key, String(value));
          }
        });
      }
      return url.toString();
    },
    [context]
  );

  const handleGoogleAuth = useCallback(() => {
    if (!config.googleAuthUrl) {
      return;
    }

    onAnalyticsEvent?.(BUCK_EVENTS.AUTH_METHOD_SELECTED, {
      [ANALYTICS_KEYS.METHOD]: 'google',
      [ANALYTICS_KEYS.LISTING_ID]: context?.listingId,
      [ANALYTICS_KEYS.PACKAGE_ID]: context?.packageId,
    });

    window.location.href = buildAuthUrl(config.googleAuthUrl);
  }, [config.googleAuthUrl, context, onAnalyticsEvent, buildAuthUrl]);

  const handleFacebookAuth = useCallback(() => {
    if (!config.facebookAuthUrl) {
      return;
    }

    onAnalyticsEvent?.(BUCK_EVENTS.AUTH_METHOD_SELECTED, {
      [ANALYTICS_KEYS.METHOD]: 'facebook',
      [ANALYTICS_KEYS.LISTING_ID]: context?.listingId,
      [ANALYTICS_KEYS.PACKAGE_ID]: context?.packageId,
    });

    window.location.href = buildAuthUrl(config.facebookAuthUrl);
  }, [config.facebookAuthUrl, context, onAnalyticsEvent, buildAuthUrl]);

  return {
    handleGoogleAuth,
    handleFacebookAuth,
  };
};
